import React, { useState, useEffect } from 'react';
import {
  getCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  Customer,
  CustomerType,
  PricingTier,
  CustomerCreate,
  CustomerUpdate,
} from '../../services/customerService';
import CustomerForm from '../../components/customer/CustomerForm';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getTierLabel, getTierBadgeColor } from '../../utils/pricingTier';

const CustomerListPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | undefined>(undefined);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<CustomerType | ''>('');
  const [filterPricingTier, setFilterPricingTier] = useState<PricingTier | ''>('');

  const currentUser = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    loadCustomers();
  }, [filterType, filterPricingTier]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params: any = {
        limit: 100,
        offset: 0,
      };
      
      if (filterType) params.type = filterType;
      if (filterPricingTier) params.pricing_tier = filterPricingTier;
      if (searchTerm) params.search = searchTerm;

      const data = await getCustomers(params);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
      alert('Gagal memuat data customer');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadCustomers();
  };

  const handleCreate = () => {
    setEditingCustomer(undefined);
    setShowForm(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setShowForm(true);
  };

  const handleSubmit = async (data: CustomerCreate | CustomerUpdate) => {
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, data as CustomerUpdate);
        alert('Customer berhasil diupdate!');
      } else {
        await createCustomer(data as CustomerCreate);
        alert('Customer berhasil dibuat!');
      }
      setShowForm(false);
      setEditingCustomer(undefined);
      loadCustomers();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Gagal menyimpan customer');
    }
  };

  const handleDelete = async (customerId: number, customerName: string) => {
    if (!confirm(`Yakin ingin nonaktifkan customer "${customerName}"?`)) {
      return;
    }

    try {
      await deleteCustomer(customerId);
      alert('Customer berhasil dinonaktifkan');
      loadCustomers();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Gagal menghapus customer');
    }
  };

  const filteredCustomers = customers.filter(customer => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      customer.name.toLowerCase().includes(search) ||
      customer.phone?.toLowerCase().includes(search) ||
      customer.email?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Data Customer</h1>
          <p className="text-gray-600 mt-1">Kelola data customer untuk garansi dan diskon</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <span className="text-xl">+</span>
          Customer Baru
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cari Customer
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Nama, HP, atau email..."
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Cari
              </button>
            </div>
          </div>

          {/* Pricing Tier Filter - Main Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipe (Pricing Tier)
            </label>
            <select
              value={filterPricingTier}
              onChange={(e) => setFilterPricingTier(e.target.value as PricingTier | '')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Semua Tipe</option>
              <option value={PricingTier.NO_DISCOUNT}>No Discount (0%)</option>
              <option value={PricingTier.BRONZE}>Bronze (3%)</option>
              <option value={PricingTier.SILVER}>Silver (5%)</option>
              <option value={PricingTier.GOLD}>Gold (10% cash / 5% tempo)</option>
              <option value={PricingTier.RETAIL_MARKUP}>Retail Markup (+35%)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">Total Customer</div>
          <div className="text-2xl font-bold text-gray-800">{filteredCustomers.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">End User</div>
          <div className="text-2xl font-bold text-blue-600">
            {filteredCustomers.filter(c => c.type === CustomerType.END_USER).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">Teknisi (Garansi)</div>
          <div className="text-2xl font-bold text-purple-600">
            {filteredCustomers.filter(c => c.type === CustomerType.TECHNICIAN).length}
          </div>
        </div>
      </div>

      {/* Customer Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : filteredCustomers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Tidak ada customer ditemukan
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Nama & Kontak
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Tipe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Pricing Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Info Tambahan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{customer.name}</div>
                      <div className="text-sm text-gray-500">{customer.phone}</div>
                      {customer.email && (
                        <div className="text-xs text-gray-400">{customer.email}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          customer.type === CustomerType.TECHNICIAN
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {customer.type === CustomerType.TECHNICIAN ? 'Teknisi' : 'End User'}
                      </span>
                      {customer.type === CustomerType.TECHNICIAN && (
                        <div className="text-xs text-gray-500 mt-1">
                          🛡️ Garansi 30 hari
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {customer.address && (
                        <div className="text-xs text-gray-600 mb-1">
                          📍 {customer.address.substring(0, 30)}...
                        </div>
                      )}
                      {customer.notes && (
                        <div className="text-xs text-gray-500">
                          📝 {customer.notes.substring(0, 30)}...
                        </div>
                      )}
                      {!customer.address && !customer.notes && (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          customer.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {customer.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => handleEdit(customer)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      {customer.is_active && (
                        <button
                          onClick={() => handleDelete(customer.id, customer.name)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Nonaktifkan
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold mb-4">
              {editingCustomer ? 'Edit Customer' : 'Customer Baru'}
            </h2>
            <CustomerForm
              customer={editingCustomer}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingCustomer(undefined);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerListPage;
